import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
} from '@mui/material';
import { Description } from '@mui/icons-material';
import { AppLayout } from '../components/layout/AppLayout';
import { BackButton } from '../components/common/BackButton';
import { useAuth } from '../hooks/useAuth';
import { useClinic } from '../hooks/useClinic';
import { createCertificateRequest } from '../services/requestsService';
import { SuccessPopup } from '../components/common/SuccessPopup';
import { useNavigate } from 'react-router-dom';
import { getUserPhotoUrl } from '../utils/getUserPhotoUrl';

export const OrderCertificatePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { branches } = useClinic();

  const [fio, setFio] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);

  const hasPatientCard = !!user?.patientClinicId;
  const currentBranch = useMemo(
    () => branches.find((b) => b.id === user?.branchId),
    [branches, user?.branchId]
  );
  const branchName = currentBranch?.name || 'Филиал';
  const userName = user?.name || 'Пользователь';
  const userInitials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || '?';

  const isFormFilled = fio.trim() && phone.trim() && amount.trim();

  useEffect(() => {
    const photoUrl = getUserPhotoUrl();
    setUserPhotoUrl(photoUrl);
  }, []);

  // Получаем время работы филиала
  const workSchedule = useMemo(() => {
    if (currentBranch?.workSchedule) {
      return currentBranch.workSchedule;
    }
    return null;
  }, [currentBranch?.workSchedule]);

  const handleSubmit = async () => {
    if (!fio.trim() || !phone.trim() || !amount.trim()) {
      setError('Заполните все поля');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const description = `ФИО: ${fio}\nТелефон: ${phone}\nСумма: ${amount}`;

    try {
      await createCertificateRequest({ description });
      setShowSuccessModal(true);
    } catch (err: any) {
      setError(err.message || 'Ошибка при отправке заявки');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasPatientCard) {
    return (
      <AppLayout title="Заказать сертификат">
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="error">
            Для заказа сертификата необходимо привязать карту клиники
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate('/profile?tab=data')}
          >
            Привязать карту
          </Button>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Заказать сертификат">
      {/* Кнопка назад */}
      <BackButton onClick={() => navigate('/cabinet')} />

      {/* Заголовок и подзаголовок */}
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '600px' }, margin: '0 auto 24px auto' }}>
        <Typography
          sx={{
            fontSize: { xs: '28px', sm: '32px' },
            fontWeight: 700,
            color: '#1C1C1E',
            lineHeight: 1.1,
            fontFamily:
              'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          Заказать сертификат
        </Typography>
        <Typography
          sx={{
            fontSize: 15,
            color: '#6B6B6F',
            mt: 1.5,
            lineHeight: 1.2,
            fontFamily:
              'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          После отправки данных с вами свяжется менеджер
        </Typography>
      </Box>

      {/* Контейнер для содержимого (центрирован) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
          {/* Карточка пользователя */}
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: '480px', md: '600px' },
              height: { xs: '90px', sm: '100px', md: '110px' },
              backgroundColor: '#FFFFFF',
              borderRadius: '22px',
              padding: { xs: '12px 16px', sm: '12px 16px', md: '16px 20px' },
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              flexShrink: 0,
              margin: '0 auto 16px auto',
            }}
          >
            <Avatar
              src={userPhotoUrl || undefined}
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#1677FF',
                color: '#FFFFFF',
                fontSize: 24,
                fontWeight: 600,
              }}
            >
              {userInitials}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 500,
                  color: '#1C1C1E',
                  lineHeight: 1.2,
                  fontFamily:
                    'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                {userName}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: '#6B6B6F',
                  mt: 0.5,
                  fontFamily:
                    'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                {branchName}
              </Typography>
            </Box>
            <Description
              sx={{
                width: 40,
                height: 40,
                backgroundColor: '#1677FF',
                color: '#FFFFFF',
                borderRadius: '50%',
                padding: '8px',
              }}
            />
          </Box>

          {/* Форма */}
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: '480px', md: '600px' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              margin: '0 auto',
            }}
          >
            <TextField
              placeholder="ФИО получателя"
              value={fio}
              onChange={(e) => setFio(e.target.value)}
              fullWidth
              inputProps={{
                style: {
                  fontSize: 14,
                  fontFamily:
                    'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                },
              }}
              sx={{
                width: '100%',
                height: { xs: 52, sm: 58 },
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  height: 52,
                  borderRadius: '22px',
                  backgroundColor: '#F5F6F8',
                  border: '1px solid rgba(33, 33, 33, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(33, 33, 33, 0.3)',
                  },
                  '& input': {
                    paddingLeft: 2,
                    color: '#212121',
                    '&::placeholder': {
                      color: 'rgba(33, 33, 33, 0.7)',
                      opacity: 1,
                    },
                  },
                  '&:focus-within': {
                    borderColor: '#1677FF',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            />

            <TextField
              placeholder="Телефон получателя"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              type="tel"
              inputProps={{
                style: {
                  fontSize: 14,
                  fontFamily:
                    'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                },
              }}
              sx={{
                width: '100%',
                height: { xs: 52, sm: 58 },
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  height: 52,
                  borderRadius: '22px',
                  backgroundColor: '#F5F6F8',
                  border: '1px solid rgba(33, 33, 33, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(33, 33, 33, 0.3)',
                  },
                  '& input': {
                    paddingLeft: 2,
                    color: '#212121',
                    '&::placeholder': {
                      color: 'rgba(33, 33, 33, 0.7)',
                      opacity: 1,
                    },
                  },
                  '&:focus-within': {
                    borderColor: '#1677FF',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            />

            <TextField
              placeholder="Сумма"
              value={amount}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setAmount(value);
              }}
              fullWidth
              inputProps={{
                style: {
                  fontSize: 14,
                  fontFamily:
                    'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                },
              }}
              sx={{
                width: '100%',
                height: { xs: 52, sm: 58 },
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  height: 52,
                  borderRadius: '22px',
                  backgroundColor: '#F5F6F8',
                  border: '1px solid rgba(33, 33, 33, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(33, 33, 33, 0.3)',
                  },
                  '& input': {
                    paddingLeft: 2,
                    color: '#212121',
                    '&::placeholder': {
                      color: 'rgba(33, 33, 33, 0.7)',
                      opacity: 1,
                    },
                  },
                  '&:focus-within': {
                    borderColor: '#1677FF',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            />

            {error && (
              <Typography
                sx={{
                  color: 'red',
                  fontSize: 14,
                  mb: 2,
                  width: '100%',
                  fontFamily:
                    'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                {error}
              </Typography>
            )}

            {/* Кнопка отправки */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormFilled || isSubmitting}
              sx={{
                width: '100%',
                height: { xs: 48, sm: 52 },
                borderRadius: '20px',
                backgroundColor: isFormFilled ? '#1677FF' : '#A9C7F5',
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: 16,
                textTransform: 'none',
                boxShadow: 'none',
                fontFamily:
                  'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                '&:hover': {
                  backgroundColor: isFormFilled ? '#0055CC' : '#A9C7F5',
                },
                cursor: isFormFilled ? 'pointer' : 'not-allowed',
              }}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </Button>
          </Box>
        </Box>

        {/* Success Modal */}
        <SuccessPopup
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            navigate('/cabinet');
          }}
          clinicName={branchName}
          workSchedule={workSchedule}
        />
    </AppLayout>
  );
};